import React, { useEffect, useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { fetchSite, adminUpdateSite, adminSiteUndo, adminSiteHistory } from "../lib/api";
import { useSettings } from "../lib/settings";
import { THEMES, FONTS, applyTheme, applyFont } from "../lib/theme";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import MediaLibraryDialog from "../components/MediaLibraryDialog";
import LivePreview from "../components/LivePreview";
import EmailSettingsForm from "../components/EmailSettingsForm";
import RepeatableList from "../components/RepeatableList";
import ImagePicker from "../components/ImagePicker";
import {
  LogOut,
  Save,
  Loader2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Check,
  Undo2,
  Eye,
  EyeOff,
} from "lucide-react";

const SeoPreviewPanel = ({ title, description, image, base, handle }) => {
  const domain = (base || (typeof window !== "undefined" ? window.location.origin : "")).replace(/^https?:\/\//, "").replace(/\/$/, "");
  return (
    <div className="mt-4 grid md:grid-cols-2 gap-4" data-testid="seo-preview-panel">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-[var(--ri-muted)] mb-1.5">Google preview</div>
        <div className="border border-[var(--ri-border)] rounded-lg p-3 bg-white">
          <div className="text-[12px] text-[#5f6368]">{domain || "yourdomain.com"}</div>
          <div className="text-[16px] text-[#1a0dab] leading-snug mt-0.5 line-clamp-1">{title || "Page title"}</div>
          <div className="text-[13px] text-[#4d5156] leading-snug mt-1 line-clamp-2">{description || "Meta description will appear here…"}</div>
        </div>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-[var(--ri-muted)] mb-1.5">Facebook / social preview</div>
        <div className="border border-[var(--ri-border)] rounded-lg overflow-hidden bg-white">
          {image ? (
            <img src={image} alt="og" className="w-full aspect-[1.91/1] object-cover bg-gray-100" />
          ) : (
            <div className="w-full aspect-[1.91/1] bg-gray-100 flex items-center justify-center text-[12px] text-[var(--ri-muted)]">No OG image</div>
          )}
          <div className="p-2.5 border-t border-[var(--ri-border)]">
            <div className="text-[10.5px] uppercase text-[var(--ri-muted)] tracking-wide">{domain || "yourdomain.com"}</div>
            <div className="text-[13px] font-semibold leading-snug mt-0.5 line-clamp-2">{title || "Page title"}</div>
            <div className="text-[11.5px] text-[var(--ri-muted)] leading-snug mt-0.5 line-clamp-2">{description || "Meta description"}</div>
            {handle && <div className="text-[10.5px] text-[var(--ri-muted)] mt-1">{handle}</div>}
          </div>
        </div>
      </div>
    </div>
  );
};



const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const Section = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div id={`section-${slugify(title)}`} className="bg-white rounded-2xl border border-[var(--ri-border)] overflow-hidden scroll-mt-24">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-[var(--ri-cream)]"
      >
        <span className="font-serif-display text-[19px]">{title}</span>
        {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
      </button>
      {open && <div className="px-5 pb-5 pt-1 border-t border-[var(--ri-border)] space-y-4">{children}</div>}
    </div>
  );
};

const SECTION_GROUPS = [
  {
    label: "Titles & text on the site",
    items: [
      "Hero section", "About section", "Happy moments (About section photo grid)", "Itinerary steps",
      "Testimonials", "Reviews / Rating bar", "FAQ", "Featured Video (YouTube)", "Our Guides slider",
      "Turkish Dizi Tours promo", "Footer & Contact info", "Footer logo strip",
    ],
  },
  {
    label: "Tour setup",
    items: ["Tour Categories", "Tour Tags"],
  },
  {
    label: "Look & feel",
    items: ["Theme & Colors", "Fonts"],
  },
  {
    label: "Technical",
    items: ["Email delivery (Gmail SMTP)", "SEO & Search visibility", "Google Ads conversion tracking"],
  },
];

const SectionJumpNav = () => {
  const [open, setOpen] = useState(true);
  const jumpTo = (title) => {
    const id = `section-${slugify(title)}`;
    const el = document.getElementById(id);
    if (!el) return;
    const button = el.querySelector("button");
    const isOpen = !!el.querySelector(".border-t");
    if (button && !isOpen) button.click();
    setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), isOpen ? 0 : 60);
  };

  return (
    <div className="bg-white rounded-2xl border border-[var(--ri-border)] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-[var(--ri-cream)]"
      >
        <span className="font-serif-display text-[19px]">Jump to a section</span>
        {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-[var(--ri-border)] grid sm:grid-cols-2 gap-5">
          {SECTION_GROUPS.map((group) => (
            <div key={group.label}>
              <div className="text-[11px] uppercase tracking-widest text-[var(--ri-muted)] mb-2 mt-3">{group.label}</div>
              <div className="flex flex-wrap gap-2">
                {group.items.map((title) => (
                  <button
                    key={title}
                    type="button"
                    onClick={() => jumpTo(title)}
                    className="text-[12.5px] px-3 py-1.5 rounded-full border border-[var(--ri-border)] hover:bg-[var(--ri-cream)] text-left"
                  >
                    {title}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SiteEditor = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { refresh: refreshSettings } = useSettings();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [hasHistory, setHasHistory] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(true);
  const [previewStamp, setPreviewStamp] = useState(0);

  useEffect(() => {
    if (!user) return;
    fetchSite().then(setData).catch(() => setData(null));
    adminSiteHistory().then((h) => setHasHistory((h || []).length > 0)).catch(() => setHasHistory(false));
  }, [user]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!data) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[var(--ri-red)]" /></div>;

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  // Live preview when theme/font change
  const previewTheme = (key) => {
    set("theme", key);
    applyTheme(key);
  };
  const previewFont = (key) => {
    set("font", key);
    applyFont(key);
  };

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = { ...data };
      delete cleaned.updated_at;
      const saved = await adminUpdateSite(cleaned);
      setData(saved);
      refreshSettings();
      setHasHistory(true);
      setPreviewStamp(Date.now());
      toast({ title: "Site saved", description: "Your changes are live." });
    } catch (err) {
      toast({ title: "Save failed", description: err?.response?.data?.detail || "Error", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const undo = async () => {
    if (!window.confirm("Undo last save? Your current settings will be replaced with the previous version.")) return;
    setUndoing(true);
    try {
      const restored = await adminSiteUndo();
      setData(restored);
      applyTheme(restored.theme || "classic");
      applyFont(restored.font || "editorial");
      refreshSettings();
      setPreviewStamp(Date.now());
      // Check if there are more history entries
      const h = await adminSiteHistory();
      setHasHistory((h || []).length > 0);
      toast({ title: "Reverted to previous version" });
    } catch (err) {
      toast({ title: "Undo failed", description: err?.response?.data?.detail || "No previous version.", variant: "destructive" });
    } finally {
      setUndoing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--ri-cream)]">
      <header className="bg-white border-b border-[var(--ri-border)] sticky top-0 z-30">
        <div className="container-x h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/admin/dashboard" className="flex items-center gap-2">
              <span className="brand-script text-[22px] text-[var(--ri-red)]">Real</span>
              <span className="font-serif-display text-[17px]">Istanbul</span>
              <span className="text-[11px] uppercase tracking-widest text-[var(--ri-muted)]">Site Editor</span>
            </Link>
            <nav className="hidden sm:flex items-center gap-2 text-[13.5px]">
              <Link to="/admin/dashboard" className="px-3 py-2 rounded-md hover:bg-black/5">Dashboard</Link>
              <span className="px-3 py-2 rounded-md bg-[var(--ri-red)] text-white">Site Editor</span>
              <a href="/" target="_blank" rel="noreferrer" className="px-3 py-2 rounded-md hover:bg-black/5 inline-flex items-center gap-1.5">
                View site <ExternalLink size={12} />
              </a>
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setPreviewOpen((v) => !v)}
              className="hidden md:inline-flex text-[13px] text-[var(--ri-muted)] hover:text-[var(--ri-ink)] items-center gap-1.5"
              title="Toggle preview"
            >
              {previewOpen ? <EyeOff size={14} /> : <Eye size={14} />}
              {previewOpen ? "Hide preview" : "Show preview"}
            </button>
            <button
              onClick={undo}
              disabled={undoing || !hasHistory}
              className="text-[13px] px-3 py-2 rounded-md border border-[var(--ri-border)] hover:bg-black/5 inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
              title={hasHistory ? "Revert to previous saved version" : "No previous version available yet"}
            >
              {undoing ? <Loader2 size={13} className="animate-spin" /> : <Undo2 size={13} />}
              Undo last save
            </button>
            <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2 text-[13.5px]">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save changes
            </button>
            <button onClick={() => { logout(); navigate("/admin/login"); }} className="text-[13px] text-[var(--ri-muted)] hover:text-[var(--ri-ink)] flex items-center gap-1.5">
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className={`w-full ${previewOpen ? "grid lg:grid-cols-[minmax(0,1fr)_460px]" : ""} gap-0`}>
        <main className="py-8 px-4 sm:px-6 lg:pl-8 lg:pr-6 space-y-4 max-w-[960px] mx-auto lg:mx-0 w-full">
        <div className="bg-[#fff7ed] border border-[#f3d5b5] rounded-2xl px-5 py-4 text-[13.5px] text-[var(--ri-ink-soft)]">
          Looking for the <strong>About, Contact, Privacy Policy, Terms & Conditions, Thank You page, or Booking Confirmation Email</strong> content?
          Those live in your dashboard's <strong>Pages tab</strong>, not here.{" "}
          <Link to="/admin/dashboard?tab=pages" className="text-[var(--ri-red)] link-underline">Go to Pages tab →</Link>
        </div>
        <SectionJumpNav />
        {/* Email delivery */}
        <Section title="Email delivery (Gmail SMTP)" defaultOpen>
          <EmailSettingsForm />
        </Section>

        {/* Theme */}
        <Section title="Theme & Colors">
          <p className="text-[13px] text-[var(--ri-muted)]">Pick a preset. Changes preview instantly, click Save to publish.</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {Object.entries(THEMES).map(([key, theme]) => (
              <button
                key={key}
                type="button"
                onClick={() => previewTheme(key)}
                className={`relative text-left rounded-xl border-2 p-4 transition-all ${
                  data.theme === key ? "border-[var(--ri-red)] shadow-md" : "border-[var(--ri-border)] hover:border-gray-400"
                }`}
              >
                {data.theme === key && (
                  <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[var(--ri-red)] text-white flex items-center justify-center">
                    <Check size={14} />
                  </span>
                )}
                <div className="flex gap-1.5 mb-3">
                  {[theme.vars["--ri-cream"], theme.vars["--ri-cream-2"], theme.vars["--ri-red"], theme.vars["--ri-ink"]].map((c, i) => (
                    <span key={i} className="w-6 h-6 rounded-full border border-black/10" style={{ background: c }} />
                  ))}
                </div>
                <div className="font-serif-display text-[17px]">{theme.label}</div>
                <div className="text-[12px] text-[var(--ri-muted)] mt-1">{theme.description}</div>
              </button>
            ))}
          </div>
        </Section>

        {/* Tour Categories */}
        <Section title="Tour Categories">
          <p className="text-[13px] text-[var(--ri-muted)]">
            Add, edit, or remove the types of tours you offer. Each one gets its own page (e.g. "/tours/cooking") listing every tour in that category.
          </p>
          <RepeatableList
            items={data.tour_categories}
            onChange={(v) => set("tour_categories", v)}
            newItem={() => ({ key: "", label: "", tagline: "", title: "", description: "", image: "" })}
            label="category"
            renderItem={(it, up) => (
              <div className="space-y-2">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[12px]">Internal key (used in the URL — no spaces)</Label>
                    <Input
                      value={it.key}
                      onChange={(e) => up({ ...it, key: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
                      placeholder="e.g. cooking"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-[12px]">Display name</Label>
                    <Input value={it.label} onChange={(e) => up({ ...it, label: e.target.value })} placeholder="e.g. Cooking Classes" className="mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-[12px]">Short tagline (shown on the category page)</Label>
                  <Input value={it.tagline} onChange={(e) => up({ ...it, tagline: e.target.value })} placeholder="e.g. Hands-on, in a real kitchen" className="mt-1" />
                </div>
                <div>
                  <Label className="text-[12px]">Page title</Label>
                  <Input value={it.title} onChange={(e) => up({ ...it, title: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label className="text-[12px]">Page description</Label>
                  <Textarea rows={2} value={it.description} onChange={(e) => up({ ...it, description: e.target.value })} className="mt-1" />
                </div>
                <ImagePicker value={it.image} onChange={(v) => up({ ...it, image: v })} label="Category page background image" />
              </div>
            )}
          />
          <p className="text-[11.5px] text-[var(--ri-muted)] mt-1">
            The internal key must be unique and match what you select as a tour's "Category" when adding or editing a tour. Renaming a key here won't automatically update tours already using the old key.
          </p>
        </Section>

        {/* Tour Tags */}
        <Section title="Tour Tags">
          <p className="text-[13px] text-[var(--ri-muted)]">
            Add, edit, or remove the type tags shown when adding or editing a tour (e.g. "Walking tour", "Food tour"). Each tour can have any number of these.
          </p>
          <RepeatableList
            items={data.tour_tags}
            onChange={(v) => set("tour_tags", v)}
            newItem={() => ({ key: "", label: "" })}
            label="tag"
            renderItem={(it, up) => (
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[12px]">Internal key (no spaces)</Label>
                  <Input
                    value={it.key}
                    onChange={(e) => up({ ...it, key: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
                    placeholder="e.g. shopping"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-[12px]">Display name</Label>
                  <Input value={it.label} onChange={(e) => up({ ...it, label: e.target.value })} placeholder="e.g. Shopping tour" className="mt-1" />
                </div>
              </div>
            )}
          />
          <p className="text-[11.5px] text-[var(--ri-muted)] mt-1">
            Renaming or removing a key here won't automatically update tours already using it.
          </p>
        </Section>

        <Section title="Fonts">
          <p className="text-[13px] text-[var(--ri-muted)]">Choose the font pairing. Previews below.</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {Object.entries(FONTS).map(([key, font]) => (
              <button
                key={key}
                type="button"
                onClick={() => previewFont(key)}
                className={`relative text-left rounded-xl border-2 p-4 transition-all ${
                  data.font === key ? "border-[var(--ri-red)] shadow-md" : "border-[var(--ri-border)] hover:border-gray-400"
                }`}
              >
                {data.font === key && (
                  <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[var(--ri-red)] text-white flex items-center justify-center">
                    <Check size={14} />
                  </span>
                )}
                <div className="text-[24px] leading-tight" style={{ fontFamily: font.display }}>
                  Istanbul
                </div>
                <div className="text-[13px] mt-1" style={{ fontFamily: font.body }}>
                  A tour with a local friend.
                </div>
                <div className="mt-3 pt-3 border-t border-[var(--ri-border)]">
                  <div className="font-medium text-[14px]">{font.label}</div>
                  <div className="text-[11.5px] text-[var(--ri-muted)] mt-0.5">{font.description}</div>
                </div>
              </button>
            ))}
          </div>
        </Section>

        {/* Hero */}
        <Section title="Hero section">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[13px]">Eyebrow (small caps above title)</Label>
              <Input value={data.hero_eyebrow || ""} onChange={(e) => set("hero_eyebrow", e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-[13px]">Primary button</Label>
              <Input value={data.hero_cta_primary || ""} onChange={(e) => set("hero_cta_primary", e.target.value)} className="mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-[13px]">Title (big headline)</Label>
            <Textarea rows={2} value={data.hero_title || ""} onChange={(e) => set("hero_title", e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label className="text-[13px]">Subtitle</Label>
            <Textarea rows={2} value={data.hero_subtitle || ""} onChange={(e) => set("hero_subtitle", e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label className="text-[13px]">Secondary button</Label>
            <Input value={data.hero_cta_secondary || ""} onChange={(e) => set("hero_cta_secondary", e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label className="text-[13px] mb-2 block">Background type</Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => set("hero_media_type", "image")}
                className={`px-4 py-2 rounded-full text-[13px] font-medium border transition-colors ${
                  (data.hero_media_type || "image") === "image"
                    ? "bg-[var(--ri-red)] text-white border-[var(--ri-red)]"
                    : "bg-white text-[var(--ri-ink-soft)] border-[var(--ri-border)]"
                }`}
              >
                Photo
              </button>
              <button
                type="button"
                onClick={() => set("hero_media_type", "video")}
                className={`px-4 py-2 rounded-full text-[13px] font-medium border transition-colors ${
                  data.hero_media_type === "video"
                    ? "bg-[var(--ri-red)] text-white border-[var(--ri-red)]"
                    : "bg-white text-[var(--ri-ink-soft)] border-[var(--ri-border)]"
                }`}
              >
                YouTube video
              </button>
            </div>
          </div>
          {data.hero_media_type === "video" ? (
            <div>
              <Label className="text-[13px]">YouTube video URL</Label>
              <Input
                value={data.hero_video_url || ""}
                onChange={(e) => set("hero_video_url", e.target.value)}
                className="mt-1"
                placeholder="https://www.youtube.com/watch?v=..."
              />
              <p className="text-[11.5px] text-[var(--ri-muted)] mt-1">
                The video plays muted and on a silent loop as the background, like a moving photo — visitors can't hear sound or control it. Paste any YouTube link (watch, youtu.be, or embed).
              </p>
            </div>
          ) : (
            <ImagePicker value={data.hero_image} onChange={(v) => set("hero_image", v)} label="Background image" />
          )}
          <div>
            <Label className="text-[13px] mb-2 block">Award badges (shown in hero, e.g. Tripadvisor awards)</Label>
            <RepeatableList
              items={data.hero_badges}
              onChange={(v) => set("hero_badges", v)}
              newItem={() => ({ label: "", year: "", platform: "" })}
              label="badge"
              renderItem={(it, up) => (
                <div className="grid sm:grid-cols-3 gap-3">
                  <Input value={it.label} onChange={(e) => up({ ...it, label: e.target.value })} placeholder="Badge text (e.g. Travelers' Choice)" />
                  <Input value={it.year} onChange={(e) => up({ ...it, year: e.target.value })} placeholder="Year (e.g. 2020)" />
                  <Input value={it.platform} onChange={(e) => up({ ...it, platform: e.target.value })} placeholder="Platform (e.g. Tripadvisor)" />
                </div>
              )}
            />
            <p className="text-[11.5px] text-[var(--ri-muted)] mt-1">
              Add, remove, or reorder as many badges as you like — new awards can be added here anytime without touching any code.
            </p>
          </div>
          <div>
            <Label className="text-[13px] mb-2 block">Stats (shown below hero)</Label>
            <RepeatableList
              items={data.hero_stats}
              onChange={(v) => set("hero_stats", v)}
              newItem={() => ({ value: "", label: "" })}
              label="stat"
              renderItem={(it, up) => (
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input value={it.value} onChange={(e) => up({ ...it, value: e.target.value })} placeholder="Value (e.g. 60,000+)" />
                  <Input value={it.label} onChange={(e) => up({ ...it, label: e.target.value })} placeholder="Label (e.g. Guests hosted)" />
                </div>
              )}
            />
          </div>
        </Section>

        {/* Reviews bar */}
        <Section title="Reviews / Rating bar">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[13px]">Overall rating (e.g. 4.95)</Label>
              <Input value={data.overall_rating || ""} onChange={(e) => set("overall_rating", e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-[13px]">Reviews note (right side of rating)</Label>
              <Input value={data.overall_reviews_note || ""} onChange={(e) => set("overall_reviews_note", e.target.value)} className="mt-1" placeholder="from 7,030+ verified guest reviews →" />
            </div>
          </div>
          <div>
            <Label className="text-[13px] mb-2 block">Platforms (Viator, Tripadvisor, ...)</Label>
            <RepeatableList
              items={data.review_platforms}
              onChange={(v) => set("review_platforms", v)}
              newItem={() => ({ name: "", rating: "", count: "" })}
              label="platform"
              renderItem={(it, up) => (
                <div className="grid sm:grid-cols-3 gap-2">
                  <Input value={it.name} onChange={(e) => up({ ...it, name: e.target.value })} placeholder="Name (e.g. Viator)" />
                  <Input value={it.rating} onChange={(e) => up({ ...it, rating: e.target.value })} placeholder="Rating (e.g. 4.95)" />
                  <Input value={it.count} onChange={(e) => up({ ...it, count: e.target.value })} placeholder="Count (e.g. 2,940+)" />
                </div>
              )}
            />
          </div>
        </Section>

        {/* About */}
        <Section title="About section">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[13px]">Eyebrow</Label>
              <Input value={data.about_eyebrow || ""} onChange={(e) => set("about_eyebrow", e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-[13px]">Title</Label>
              <Input value={data.about_title || ""} onChange={(e) => set("about_title", e.target.value)} className="mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-[13px]">Paragraphs (one per line, blank line for new paragraph)</Label>
            <Textarea
              rows={8}
              value={(data.about_paragraphs || []).join("\n\n")}
              onChange={(e) => set("about_paragraphs", e.target.value.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean))}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-[13px] mb-2 block">Stats</Label>
            <RepeatableList
              items={data.about_stats}
              onChange={(v) => set("about_stats", v)}
              newItem={() => ({ value: "", label: "" })}
              label="stat"
              renderItem={(it, up) => (
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input value={it.value} onChange={(e) => up({ ...it, value: e.target.value })} placeholder="Value" />
                  <Input value={it.label} onChange={(e) => up({ ...it, label: e.target.value })} placeholder="Label" />
                </div>
              )}
            />
          </div>
        </Section>

        {/* Happy moments */}
        <Section title="Happy moments (About section photo grid)">
          <div>
            <Label className="text-[13px]">Caption below photos</Label>
            <Input value={data.happy_moments_title || ""} onChange={(e) => set("happy_moments_title", e.target.value)} className="mt-1" placeholder="Happy moments from our tours." />
          </div>
          <div>
            <Label className="text-[13px] mb-2 block">Photos (up to 9 shown on homepage as a 3×3 grid)</Label>
            <p className="text-[11.5px] text-[var(--ri-muted)] mb-2">Leave empty to show the default avatar mosaic. Add real guest photos here to replace it.</p>
            <RepeatableList
              items={data.happy_moments}
              onChange={(v) => set("happy_moments", v)}
              newItem={() => ""}
              label="photo"
              renderItem={(it, up) => (
                <ImagePicker value={it} onChange={(v) => up(v)} label="Photo" />
              )}
            />
          </div>
        </Section>

        {/* Itinerary */}
        <Section title="Itinerary steps">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[13px]">Eyebrow</Label>
              <Input value={data.itinerary_eyebrow || ""} onChange={(e) => set("itinerary_eyebrow", e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-[13px]">Title</Label>
              <Input value={data.itinerary_title || ""} onChange={(e) => set("itinerary_title", e.target.value)} className="mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-[13px]">Intro paragraph</Label>
            <Textarea rows={3} value={data.itinerary_intro || ""} onChange={(e) => set("itinerary_intro", e.target.value)} className="mt-1" />
          </div>
          <RepeatableList
            items={data.itinerary_steps}
            onChange={(v) => set("itinerary_steps", v)}
            newItem={() => ({ side: "", step: (data.itinerary_steps?.length || 0) + 1, title: "", description: "", image: "", highlights: "" })}
            label="step"
            renderItem={(it, up) => (
              <div className="space-y-2">
                <div className="grid sm:grid-cols-3 gap-2">
                  <Input value={it.side} onChange={(e) => up({ ...it, side: e.target.value })} placeholder="Side (European side)" />
                  <Input type="number" value={it.step} onChange={(e) => up({ ...it, step: Number(e.target.value) })} placeholder="Step #" />
                  <Input value={it.title} onChange={(e) => up({ ...it, title: e.target.value })} placeholder="Title" className="sm:col-span-1" />
                </div>
                <Textarea rows={3} value={it.description} onChange={(e) => up({ ...it, description: e.target.value })} placeholder="Description" />
                <Input value={it.highlights} onChange={(e) => up({ ...it, highlights: e.target.value })} placeholder="Highlights (Blue Mosque · Hippodrome · ...)" />
                <ImagePicker value={it.image} onChange={(v) => up({ ...it, image: v })} label="Step image" />
              </div>
            )}
          />
        </Section>

        {/* Dizi Tours */}
        <Section title="Turkish Dizi Tours promo">
          <label className="flex items-center gap-2 text-[14px] mb-3">
            <input
              type="checkbox"
              checked={data.show_dizi_tours !== false}
              onChange={(e) => set("show_dizi_tours", e.target.checked)}
            />
            Show this section on the homepage
          </label>
          <div className="grid sm:grid-cols-2 gap-3">
            <Input value={data.dizi_tours_eyebrow || ""} onChange={(e) => set("dizi_tours_eyebrow", e.target.value)} placeholder="Eyebrow (e.g. As seen on screen)" />
            <Input value={data.dizi_tours_cta_label || ""} onChange={(e) => set("dizi_tours_cta_label", e.target.value)} placeholder="Button text (e.g. Plan your Dizi tour)" />
          </div>
          <Input className="mt-3" value={data.dizi_tours_title || ""} onChange={(e) => set("dizi_tours_title", e.target.value)} placeholder="Title" />
          <Textarea rows={4} className="mt-3" value={data.dizi_tours_text || ""} onChange={(e) => set("dizi_tours_text", e.target.value)} placeholder="Description text" />
          <p className="text-[11.5px] text-[var(--ri-muted)] mt-1 mb-3">
            The button sends visitors to a dedicated request form (name, email, WhatsApp number, tour date, hotel, favourite dizis, and a message) — submissions land in your Inquiries list, same as the contact form.
          </p>
          <div>
            <Label className="text-[13px] mb-2 block">Photos</Label>
            <RepeatableList
              items={data.dizi_tours_images}
              onChange={(v) => set("dizi_tours_images", v)}
              newItem={() => ({ image: "", caption: "" })}
              label="photo"
              renderItem={(it, up) => (
                <div className="space-y-2">
                  <ImagePicker value={it.image} onChange={(v) => up({ ...it, image: v })} label="Photo" />
                  <Input value={it.caption} onChange={(e) => up({ ...it, caption: e.target.value })} placeholder="Caption (optional)" />
                </div>
              )}
            />
            <p className="text-[11.5px] text-[var(--ri-muted)] mt-1">
              The first photo shows large at the top; the rest appear as a grid below it. Currently using placeholder stock photos — replace with your own anytime by uploading here.
            </p>
          </div>
        </Section>

        {/* Guides */}
        <Section title="Our Guides slider" defaultOpen={true}>
          <div>
            <Label className="text-[13px]">Section title</Label>
            <Input value={data.guides_section_title || ""} onChange={(e) => set("guides_section_title", e.target.value)} className="mt-1" placeholder="Our Guides" />
          </div>
          <div className="mt-3">
            <Label className="text-[13px]">Short description</Label>
            <Textarea rows={2} value={data.guides_section_intro || ""} onChange={(e) => set("guides_section_intro", e.target.value)} className="mt-1" />
          </div>
          <div className="mt-4">
            <Label className="text-[13px] mb-2 block">Guides</Label>
            <RepeatableList
              items={data.guides_list}
              onChange={(v) => set("guides_list", v)}
              newItem={() => ({ image: "", name: "", bio: "" })}
              label="guide"
              renderItem={(it, up) => (
                <div className="space-y-2">
                  <ImagePicker value={it.image} onChange={(v) => up({ ...it, image: v })} label="Photo" />
                  <Input value={it.name} onChange={(e) => up({ ...it, name: e.target.value })} placeholder="Name" />
                  <Textarea rows={2} value={it.bio} onChange={(e) => up({ ...it, bio: e.target.value })} placeholder="Short bio (1-2 sentences)" />
                </div>
              )}
            />
            <p className="text-[11.5px] text-[var(--ri-muted)] mt-1">
              Add, remove, or reorder guides here — they scroll as a slider on the homepage, so adding more never makes the page longer.
            </p>
          </div>
        </Section>

        {/* Testimonials */}
        <Section title="Testimonials">
          <div className="grid sm:grid-cols-2 gap-3">
            <Input value={data.testimonials_eyebrow || ""} onChange={(e) => set("testimonials_eyebrow", e.target.value)} placeholder="Eyebrow" />
            <Input value={data.testimonials_title || ""} onChange={(e) => set("testimonials_title", e.target.value)} placeholder="Title" />
          </div>
          <Textarea rows={3} value={data.testimonials_intro || ""} onChange={(e) => set("testimonials_intro", e.target.value)} placeholder="Intro" />
          <RepeatableList
            items={data.testimonials}
            onChange={(v) => set("testimonials", v)}
            newItem={() => ({ title: "", quote: "", author: "", source: "" })}
            label="testimonial"
            renderItem={(it, up) => (
              <div className="space-y-2">
                <Input value={it.title} onChange={(e) => up({ ...it, title: e.target.value })} placeholder="Short title" />
                <Textarea rows={2} value={it.quote} onChange={(e) => up({ ...it, quote: e.target.value })} placeholder="Quote" />
                <div className="grid sm:grid-cols-2 gap-2">
                  <Input value={it.author} onChange={(e) => up({ ...it, author: e.target.value })} placeholder="Author (e.g. T.H.)" />
                  <Input value={it.source} onChange={(e) => up({ ...it, source: e.target.value })} placeholder="Source (e.g. via Tripadvisor)" />
                </div>
              </div>
            )}
          />
        </Section>

        {/* Featured Video */}
        <Section title="Featured Video (YouTube)">
          <label className="flex items-center gap-2 text-[14px] mb-1">
            <input
              type="checkbox"
              checked={!!data.show_featured_video}
              onChange={(e) => set("show_featured_video", e.target.checked)}
            />
            Show this section on the homepage
          </label>
          <p className="text-[11.5px] text-[var(--ri-muted)] mb-3">
            Stays hidden until you check this box — useful while you're still filling in the details below.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Input value={data.featured_video_eyebrow || ""} onChange={(e) => set("featured_video_eyebrow", e.target.value)} placeholder="Eyebrow (e.g. Featured on YouTube)" />
            <Input value={data.featured_video_creator || ""} onChange={(e) => set("featured_video_creator", e.target.value)} placeholder="Creator credit (e.g. Kara and Nate · Travel YouTubers)" />
          </div>
          <Input className="mt-3" value={data.featured_video_title || ""} onChange={(e) => set("featured_video_title", e.target.value)} placeholder="Title" />
          <Textarea rows={3} className="mt-3" value={data.featured_video_description || ""} onChange={(e) => set("featured_video_description", e.target.value)} placeholder="Description" />
          <div className="mt-3">
            <Label className="text-[13px]">YouTube video URL</Label>
            <Input
              className="mt-1"
              value={data.featured_video_youtube_url || ""}
              onChange={(e) => set("featured_video_youtube_url", e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
            />
            <p className="text-[11.5px] text-[var(--ri-muted)] mt-1">
              Used for both the thumbnail image and the "Watch on YouTube" link. Any YouTube link format works (watch, youtu.be, or embed).
            </p>
          </div>
        </Section>

        {/* FAQ */}
        <Section title="FAQ">
          <div className="grid sm:grid-cols-2 gap-3">
            <Input value={data.faq_eyebrow || ""} onChange={(e) => set("faq_eyebrow", e.target.value)} placeholder="Eyebrow" />
            <Input value={data.faq_title || ""} onChange={(e) => set("faq_title", e.target.value)} placeholder="Title" />
          </div>
          <RepeatableList
            items={data.faqs}
            onChange={(v) => set("faqs", v)}
            newItem={() => ({ q: "", a: "" })}
            label="question"
            renderItem={(it, up) => (
              <div className="space-y-2">
                <Input value={it.q} onChange={(e) => up({ ...it, q: e.target.value })} placeholder="Question" />
                <Textarea rows={3} value={it.a} onChange={(e) => up({ ...it, a: e.target.value })} placeholder="Answer" />
              </div>
            )}
          />
        </Section>

        {/* Footer & Contact */}
        <Section title="Footer & Contact info" defaultOpen={true}>
          <div>
            <Label className="text-[13px]">Footer tagline</Label>
            <Textarea rows={3} value={data.footer_tagline || ""} onChange={(e) => set("footer_tagline", e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label className="text-[13px]">Meeting point address</Label>
            <Input value={data.meeting_point || ""} onChange={(e) => set("meeting_point", e.target.value)} className="mt-1" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[13px]">Email</Label>
              <Input value={data.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-[13px]">Phone / WhatsApp</Label>
              <Input value={data.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} className="mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-[13px] mb-2 block">Social & review links (footer icons)</Label>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px]">Instagram URL</Label>
                <Input value={data.social_instagram_url || ""} onChange={(e) => set("social_instagram_url", e.target.value)} className="mt-1" placeholder="https://www.instagram.com/istanbulfreewalkingtours/" />
              </div>
              <div>
                <Label className="text-[12px]">Facebook URL</Label>
                <Input value={data.social_facebook_url || ""} onChange={(e) => set("social_facebook_url", e.target.value)} className="mt-1" placeholder="https://www.facebook.com/istanbulfreewalkingtoursturkey/" />
              </div>
              <div>
                <Label className="text-[12px]">YouTube URL (optional)</Label>
                <Input value={data.social_youtube_url || ""} onChange={(e) => set("social_youtube_url", e.target.value)} className="mt-1" placeholder="Leave blank to hide the icon" />
              </div>
              <div>
                <Label className="text-[12px]">Tripadvisor URL</Label>
                <Input value={data.social_tripadvisor_url || ""} onChange={(e) => set("social_tripadvisor_url", e.target.value)} className="mt-1" placeholder="https://www.tripadvisor.com/..." />
              </div>
              <div>
                <Label className="text-[12px]">Viator profile URL (optional)</Label>
                <Input value={data.social_viator_url || ""} onChange={(e) => set("social_viator_url", e.target.value)} className="mt-1" placeholder="Leave blank to hide the icon" />
              </div>
            </div>
          </div>
          <div>
            <Label className="text-[13px]">WhatsApp business number (used for "Open in WhatsApp" buttons)</Label>
            <Input
              value={data.whatsapp_number || ""}
              onChange={(e) => set("whatsapp_number", e.target.value)}
              className="mt-1"
              placeholder="+905417692277"
              data-testid="whatsapp-number-input"
            />
            <p className="text-[11.5px] text-[var(--ri-muted)] mt-1">
              Enter with country code. This is the fallback used when a guest submits without a phone number.
            </p>
          </div>

          <div className="mt-2 pt-3 border-t border-[var(--ri-border)]">
            <div className="text-[11.5px] uppercase tracking-widest text-[var(--ri-muted)] mb-2">Homepage "Chat on WhatsApp" button</div>
            <label className="flex items-center gap-2 text-[14px] mb-2">
              <input
                type="checkbox"
                checked={data.show_whatsapp_hero_button !== false}
                onChange={(e) => set("show_whatsapp_hero_button", e.target.checked)}
              />
              Show on homepage
            </label>
            <Label className="text-[13px]">Pre-filled message</Label>
            <Textarea
              rows={2}
              className="mt-1"
              value={data.whatsapp_hero_message || ""}
              onChange={(e) => set("whatsapp_hero_message", e.target.value)}
              placeholder="Hi! I'd like to know more about your Istanbul tours."
            />
          </div>

          <div className="mt-2 pt-3 border-t border-[var(--ri-border)]">
            <div className="text-[11.5px] uppercase tracking-widest text-[var(--ri-muted)] mb-2">Sticky WhatsApp bubble</div>
            <label className="flex items-center gap-2 text-[14px]">
              <input
                type="checkbox"
                checked={data.show_sticky_whatsapp !== false}
                onChange={(e) => set("show_sticky_whatsapp", e.target.checked)}
              />
              Show a floating WhatsApp button on every page
            </label>
            <p className="mt-1 text-[11.5px] text-[var(--ri-muted)]">Uses the same phone number and pre-filled message as the buttons above.</p>
          </div>

          <div className="mt-2 pt-3 border-t border-[var(--ri-border)]">
            <div className="text-[11.5px] uppercase tracking-widest text-[var(--ri-muted)] mb-2">Tour page "Book with WhatsApp" button</div>
            <label className="flex items-center gap-2 text-[14px] mb-2">
              <input
                type="checkbox"
                checked={data.show_whatsapp_tour_button !== false}
                onChange={(e) => set("show_whatsapp_tour_button", e.target.checked)}
              />
              Show on every tour's detail page
            </label>
            <Label className="text-[13px]">Pre-filled message</Label>
            <Textarea
              rows={2}
              className="mt-1"
              value={data.whatsapp_tour_message_template || ""}
              onChange={(e) => set("whatsapp_tour_message_template", e.target.value)}
              placeholder="Hi! I'd like to book the {tour} tour."
            />
            <p className="text-[11.5px] text-[var(--ri-muted)] mt-1">
              Use <code>{"{tour}"}</code> anywhere in the message — it's automatically replaced with the actual tour's name.
            </p>
          </div>
        </Section>

        {/* Footer logos */}
        <Section title="Footer logo strip">
          <p className="text-[12.5px] text-[var(--ri-muted)] -mt-1 mb-2">
            Shown under your tagline in the footer — e.g. press mentions, award badges, or partner logos. Every photo you upload is automatically resized to the same small, consistent square, so it doesn't matter what size or shape the original image is.
          </p>
          <RepeatableList
            items={data.footer_logos}
            onChange={(v) => set("footer_logos", v)}
            newItem={() => ({ image: "", text: "", link: "" })}
            label="logo"
            renderItem={(it, up) => (
              <div className="space-y-3">
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <ImagePicker value={it.image} onChange={(v) => up({ ...it, image: v })} label="Logo image" />
                  </div>
                  <div className="flex-1">
                    <Label className="text-[13px]">Text next to it (optional)</Label>
                    <Input value={it.text} onChange={(e) => up({ ...it, text: e.target.value })} className="mt-1" placeholder="e.g. As featured in Condé Nast Traveler" />
                  </div>
                </div>
                <div>
                  <Label className="text-[13px]">Link (optional)</Label>
                  <Input value={it.link || ""} onChange={(e) => up({ ...it, link: e.target.value })} className="mt-1" placeholder="https://... — makes the logo clickable, opens in a new tab" />
                </div>
              </div>
            )}
          />
        </Section>

        {/* Privacy Policy */}
        {/* SEO */}
        <Section title="SEO & Search visibility" defaultOpen={true}>
          <div className="text-[12.5px] text-[var(--ri-muted)] -mt-1 mb-2">
            These fields populate the &lt;title&gt;, meta description, Open Graph &amp; Twitter cards on every public page. Per-tour and per-post SEO overrides live in the tour and blog editors.
          </div>
          <div>
            <Label className="text-[13px]">Site title (default across pages)</Label>
            <Input
              value={data.seo_site_title || ""}
              onChange={(e) => set("seo_site_title", e.target.value)}
              className="mt-1"
              placeholder="Free Istanbul Tours · Small-group Istanbul tours since 2014"
              data-testid="seo-site-title"
              maxLength={70}
            />
            <div className="text-[11px] text-[var(--ri-muted)] mt-1 flex justify-between">
              <span>Recommended 50–60 characters</span>
              <span className={((data.seo_site_title || "").length) > 60 ? "text-[var(--ri-red)]" : ""}>
                {(data.seo_site_title || "").length}/60
              </span>
            </div>
          </div>
          <div>
            <Label className="text-[13px]">Meta description (default)</Label>
            <Textarea
              rows={3}
              value={data.seo_meta_description || ""}
              onChange={(e) => set("seo_meta_description", e.target.value)}
              className="mt-1"
              placeholder="Award-winning small-group Istanbul tours…"
              data-testid="seo-meta-description"
              maxLength={200}
            />
            <div className="text-[11px] text-[var(--ri-muted)] mt-1 flex justify-between">
              <span>Recommended 140–160 characters</span>
              <span className={((data.seo_meta_description || "").length) > 160 ? "text-[var(--ri-red)]" : ""}>
                {(data.seo_meta_description || "").length}/160
              </span>
            </div>
          </div>
          <div>
            <Label className="text-[13px]">Meta keywords (comma separated)</Label>
            <Input
              value={data.seo_meta_keywords || ""}
              onChange={(e) => set("seo_meta_keywords", e.target.value)}
              className="mt-1"
              placeholder="istanbul tours, hagia sophia tour, bosphorus cruise"
              data-testid="seo-meta-keywords"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[13px]">Organization / Brand name</Label>
              <Input
                value={data.seo_org_name || ""}
                onChange={(e) => set("seo_org_name", e.target.value)}
                className="mt-1"
                data-testid="seo-org-name"
              />
            </div>
            <div>
              <Label className="text-[13px]">Twitter / X handle (with @)</Label>
              <Input
                value={data.seo_twitter_handle || ""}
                onChange={(e) => set("seo_twitter_handle", e.target.value)}
                className="mt-1"
                placeholder="@istanbulfreewalkingtours"
                data-testid="seo-twitter-handle"
              />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[13px]">Canonical base URL</Label>
              <Input
                value={data.seo_canonical_base || ""}
                onChange={(e) => set("seo_canonical_base", e.target.value)}
                className="mt-1"
                placeholder="https://www.istanbulfreewalkingtours.com"
                data-testid="seo-canonical-base"
              />
              <p className="text-[11px] text-[var(--ri-muted)] mt-1">Used in sitemap.xml, canonical links and OG URL. Include https:// and no trailing slash.</p>
            </div>
            <div>
              <Label className="text-[13px]">Robots directive</Label>
              <select
                value={data.seo_robots || "index, follow"}
                onChange={(e) => set("seo_robots", e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-md border border-[var(--ri-border)] bg-white text-[14px]"
                data-testid="seo-robots"
              >
                <option value="index, follow">index, follow (recommended)</option>
                <option value="noindex, follow">noindex, follow</option>
                <option value="index, nofollow">index, nofollow</option>
                <option value="noindex, nofollow">noindex, nofollow (hide entire site)</option>
              </select>
            </div>
          </div>
          <div>
            <ImagePicker value={data.seo_og_image || ""} onChange={(v) => set("seo_og_image", v)} label="Default social share image (1200×630 recommended)" />
          </div>
          <div>
            <ImagePicker value={data.seo_favicon || ""} onChange={(v) => set("seo_favicon", v)} label="Favicon (32×32 or SVG)" />
          </div>

          {/* Homepage overrides */}
          <div className="pt-2 mt-2 border-t border-[var(--ri-border)]">
            <div className="text-[12.5px] uppercase tracking-widest text-[var(--ri-muted)] mb-3">Homepage overrides (optional)</div>
            <div className="space-y-3">
              <div>
                <Label className="text-[13px]">Homepage title override</Label>
                <Input value={data.seo_home_title || ""} onChange={(e) => set("seo_home_title", e.target.value)} className="mt-1" data-testid="seo-home-title" />
              </div>
              <div>
                <Label className="text-[13px]">Homepage description override</Label>
                <Textarea rows={2} value={data.seo_home_description || ""} onChange={(e) => set("seo_home_description", e.target.value)} className="mt-1" data-testid="seo-home-description" />
              </div>
              <ImagePicker value={data.seo_home_og_image || ""} onChange={(v) => set("seo_home_og_image", v)} label="Homepage social image override" />
            </div>
          </div>

          {/* Preview */}
          <SeoPreviewPanel
            title={data.seo_home_title || data.seo_site_title}
            description={data.seo_home_description || data.seo_meta_description}
            image={data.seo_home_og_image || data.seo_og_image}
            base={data.seo_canonical_base}
            handle={data.seo_twitter_handle}
          />

          {/* Sitemap link */}
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <a
              href="/api/sitemap.xml"
              target="_blank"
              rel="noreferrer"
              className="text-[13px] inline-flex items-center gap-1 text-[var(--ri-red)] hover:underline"
              data-testid="seo-sitemap-link"
            >
              View sitemap.xml <ExternalLink size={12} />
            </a>
            <a
              href="/robots.txt"
              target="_blank"
              rel="noreferrer"
              className="text-[13px] inline-flex items-center gap-1 text-[var(--ri-red)] hover:underline"
              data-testid="seo-robots-link"
            >
              View robots.txt <ExternalLink size={12} />
            </a>
            <span className="text-[11.5px] text-[var(--ri-muted)]">Submit the sitemap URL in Google Search Console.</span>
          </div>
        </Section>

        {/* Google Ads */}
        <Section title="Google Ads conversion tracking">
          <label className="flex items-center gap-2 text-[14px] mb-1">
            <input
              type="checkbox"
              checked={!!data.google_ads_enabled}
              onChange={(e) => set("google_ads_enabled", e.target.checked)}
            />
            Enable Google Ads tracking
          </label>
          <p className="text-[11.5px] text-[var(--ri-muted)] mb-3">
            Once enabled, we automatically track two actions as conversions: a visitor submitting a booking request, and a visitor clicking a "Chat on WhatsApp" or "Book with WhatsApp" button anywhere on the site.
          </p>

          <div>
            <Label className="text-[13px]">Google Ads ID</Label>
            <Input
              value={data.google_ads_id || ""}
              onChange={(e) => set("google_ads_id", e.target.value)}
              className="mt-1"
              placeholder="AW-123456789"
            />
            <p className="text-[11.5px] text-[var(--ri-muted)] mt-1">
              Find this in Google Ads under Tools &amp; Settings → Conversions, or in your account's Conversion ID (starts with "AW-").
            </p>
          </div>

          <div className="mt-3">
            <Label className="text-[13px]">Booking conversion — "send to" value</Label>
            <Input
              value={data.google_ads_booking_send_to || ""}
              onChange={(e) => set("google_ads_booking_send_to", e.target.value)}
              className="mt-1"
              placeholder="AW-123456789/AbCdEfGhIjKlMnOp"
            />
            <p className="text-[11.5px] text-[var(--ri-muted)] mt-1">
              Fires when a visitor successfully submits a booking request on any tour. In Google Ads, create a conversion action (e.g. "Booking submitted") and paste its full ID/label pair here.
            </p>
          </div>

          <div className="mt-3">
            <Label className="text-[13px]">WhatsApp click conversion — "send to" value</Label>
            <Input
              value={data.google_ads_whatsapp_send_to || ""}
              onChange={(e) => set("google_ads_whatsapp_send_to", e.target.value)}
              className="mt-1"
              placeholder="AW-123456789/QrStUvWxYzAbCdEf"
            />
            <p className="text-[11.5px] text-[var(--ri-muted)] mt-1">
              Fires when a visitor clicks a WhatsApp button — the homepage "Chat on WhatsApp" button, or the "Book with WhatsApp" button on any tour page. Create a separate conversion action for this in Google Ads (e.g. "WhatsApp click") and paste its ID/label pair here.
            </p>
          </div>

          <div className="mt-3 rounded-lg bg-[var(--ri-cream)] border border-[var(--ri-border)] p-3 text-[12px] text-[var(--ri-ink-soft)] leading-[1.6]">
            <strong>How to find the "send to" value:</strong> In Google Ads, go to Goals → Conversions → click your conversion action → "Tag setup" → "Use Google Tag Manager" or "Install the tag yourself". You'll see a line like <code>send_to: 'AW-123456789/AbCdEfGhIjKlMnOp'</code> — copy everything inside the quotes into the field above.
          </div>
        </Section>

        <div className="sticky bottom-4 flex justify-end pt-4">
          <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2 shadow-xl">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save changes
          </button>
        </div>
      </main>

      {previewOpen && (
        <aside className="hidden lg:block sticky top-16 h-[calc(100vh-64px)] py-4 pr-4">
          <div className="h-full flex flex-col">
            <div className="flex items-center justify-between px-1 mb-2">
              <div className="text-[11px] uppercase tracking-widest text-[var(--ri-muted)]">Live preview</div>
              <div className="text-[10.5px] text-[var(--ri-muted)]">
                Save to refresh · scaled 55%
              </div>
            </div>
            <div className="flex-1 relative overflow-hidden rounded-xl border border-[var(--ri-border)] bg-white">
              <div
                style={{
                  width: "820px",
                  height: "calc((100vh - 120px) / 0.55)",
                  transform: "scale(0.55)",
                  transformOrigin: "top left",
                }}
              >
                <LivePreview src="/" stamp={previewStamp} />
              </div>
            </div>
          </div>
        </aside>
      )}
      </div>
    </div>
  );
};

export default SiteEditor;
